import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, Droplet, Heart, ChevronRight, Plus, Brain, Leaf, Activity, Moon, Bookmark, Star, CircleCheck } from "lucide-react";
import useMetrics from "../hooks/useMetrics";
import { useAuth } from "../contexts/AuthContext";
import { useVitals } from "../contexts/VitalsContext";
import api from "../lib/api";

function TinyBars({ values, color = "#4b5563", faint = "#d1d5db" }) {
  const labels = ["M", "T", "W", "T", "F", "S", "S"];

  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
      {values.map((height, i) => (
        <div key={`${labels[i]}-${i}`} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          <div
            style={{
              width: 7,
              height,
              borderRadius: 8,
              background: i === values.length - 1 ? color : i % 2 === 0 ? color : faint,
            }}
          />
          <span style={{ fontSize: 10, color: "#6b7280" }}>{labels[i]}</span>
        </div>
      ))}
    </div>
  );
}

function TinyLine({ points, color = "#f43f5e" }) {
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const width = 128;
  const height = 52;

  const plot = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * width;
      const y = height - ((p - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(" ");

  const area = `${plot} ${width},${height} 0,${height}`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polygon points={area} fill="rgba(244,63,94,0.12)" />
      <polyline fill="none" stroke={color} strokeWidth="2.5" points={plot} />
    </svg>
  );
}

function MetricCard({ icon, title, value, unit, subtitle, rightViz, accent = "#4b5563", onClick }) {
  return (
    <div
      onClick={onClick}
      onKeyDown={(event) => {
        if (!onClick) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick();
        }
      }}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      style={{
        background: "white",
        border: "1px solid #e2e6ec",
        borderRadius: 24,
        padding: 16,
        marginBottom: 10,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {icon}
          <span style={{ fontSize: 15, fontWeight: 600, color: "#273244" }}>{title}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#4b5563", fontSize: 15 }}>
          <span>Today</span>
          <ChevronRight size={16} />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#1f2937", lineHeight: 1 }}>{value}</span>
            <span style={{ fontSize: 15, color: "#374151", fontWeight: 500 }}>{unit}</span>
          </div>
          <div style={{ color: "#6b7280", marginTop: 4, fontSize: 15 }}>{subtitle}</div>
        </div>

        <div style={{ minWidth: 130 }}>{rightViz}</div>
      </div>
    </div>
  );
}

function OverviewRow({ icon, title, status, statusColor, progress, progressColor, note }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "32px 1fr auto", gap: 10, alignItems: "center", marginBottom: 14 }}>
      <div style={{ color: "#5b6472", display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</div>

      <div>
        <div style={{ fontSize: 18, fontWeight: 600, color: "#1f2937", marginBottom: 6 }}>{title}</div>
        <div style={{ width: "100%", height: 5, borderRadius: 999, background: "#d8dce3", overflow: "hidden", marginBottom: 6 }}>
          <div style={{ width: `${progress}%`, height: "100%", borderRadius: 999, background: progressColor }} />
        </div>
        <div style={{ fontSize: 13, color: "#5f6776" }}>{note}</div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, color: "#4b5563", alignSelf: "start", marginTop: 2 }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusColor }} />
        {status}
      </div>
    </div>
  );
}

export default function HealthSnapshot() {
  const navigate = useNavigate();
  const { metrics, fetchDashboardMetrics } = useMetrics();
  const { assessments } = useAuth();
  const { vitals } = useVitals();
  const [pharmacies, setPharmacies] = useState([]);

  useEffect(() => {
    fetchDashboardMetrics();
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadPharmacies = async () => {
      try {
        const res = await api.get("/api/pharmacy");
        if (!mounted) return;
        setPharmacies(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        if (!mounted) return;
        setPharmacies([]);
      }
    };

    loadPharmacies();
    return () => {
      mounted = false;
    };
  }, []);

  const latestAssessment = Array.isArray(assessments) && assessments.length > 0
    ? [...assessments].sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))[0]
    : null;
  const latestVitals = metrics?.vitals?.latest || {};

  const unwrapVital = (vital) => {
    if (vital === null || vital === undefined || vital === "") return null;
    if (typeof vital === "number" || typeof vital === "string") return vital;
    if (Array.isArray(vital)) {
      const last = vital[vital.length - 1];
      if (typeof last === "object" && last !== null && "value" in last) return last.value;
      if (typeof last === "number") return last;
      if (typeof last === "string") return last;
    }
    if (typeof vital === "object" && vital !== null) {
      if (typeof vital.value === "number") return vital.value;
    }
    return null;
  };

  const unwrapBloodPressure = (bp) => {
    if (!bp) return null;
    if (typeof bp === "string") return bp;
    if (Array.isArray(bp)) {
      const last = bp[bp.length - 1];
      if (typeof last === "object" && last !== null) {
        if (last.raw) return last.raw;
        if (last.systolic && last.diastolic) return `${last.systolic}/${last.diastolic}`;
      }
    }
    if (typeof bp === "object") {
      if (bp.raw) return bp.raw;
      if (bp.systolic && bp.diastolic) return `${bp.systolic}/${bp.diastolic}`;
    }
    return null;
  };

  const weightValue = unwrapVital(latestVitals?.weight) ?? vitals?.weight ?? latestAssessment?.weightKg ?? latestAssessment?.weight ?? 70;
  const bloodPressure = unwrapBloodPressure(latestVitals?.bloodPressure) ?? vitals?.bp ?? "128/80";
  const heartRate = unwrapVital(latestVitals?.heartRate) ?? vitals?.heart ?? 72;
  const displayWeight = Number.isFinite(Number(weightValue)) ? Number(weightValue).toFixed(2) : "--";
  const featuredPharmacy = pharmacies[0] || null;

  const pharmacyImage =
    featuredPharmacy?.coverImage ||
    featuredPharmacy?.banner ||
    featuredPharmacy?.image ||
    featuredPharmacy?.logo ||
    "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=900&q=80";

  const pharmacyName = featuredPharmacy?.name || "Dr. Stone Pharmacy (NY Branch)";
  const pharmacyAddress = featuredPharmacy?.address
    ? `${featuredPharmacy.address}${featuredPharmacy?.city ? `, ${featuredPharmacy.city}` : ""}`
    : "Elementary Street 123, NY (12.8km)";
  const pharmacyRating = featuredPharmacy?.rating || "4.2";
  const pharmacyReviews = featuredPharmacy?.reviews || "125";
  const pharmacyTag = featuredPharmacy?.category || featuredPharmacy?.speciality || "Personal Care";

  return (
    <div style={{ marginBottom: 20, marginTop: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <h4 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "#1f2937" }}>Health Metrics</h4>
        <button
          onClick={() => navigate("/health-metrics")}
          style={{ border: "none", background: "transparent", color: "var(--primary)", fontSize: 16, fontWeight: 600, padding: 0 }}
        >
          See All
        </button>
      </div>

      <MetricCard
        icon={<Scale size={18} color="#596273" />}
        title="Weight"
        value={displayWeight}
        unit="kg"
        subtitle="Stable weight"
        accent="#4b5563"
        rightViz={<TinyBars values={[22, 14, 30, 34, 18, 10, 18]} color="#4b5563" faint="#cfd4dd" />}
        onClick={() => navigate("/metrics/weight")}
      />

      <MetricCard
        icon={<Droplet size={18} color="#8b5cf6" fill="#8b5cf6" />}
        title="Blood Pressure"
        value={bloodPressure}
        unit="mmHg"
        subtitle="Stable Range"
        accent="#8b5cf6"
        rightViz={<TinyBars values={[10, 22, 14, 32, 34, 32, 14]} color="#8b5cf6" faint="#ded6fb" />}
        onClick={() => navigate("/metrics/blood-pressure")}
      />

      <MetricCard
        icon={<Heart size={18} color="#f43f5e" fill="#f43f5e" />}
        title="Heart Rate"
        value={heartRate}
        unit="bpm"
        subtitle="Resting Rate"
        accent="#f43f5e"
        rightViz={<TinyLine points={[74, 74, 68, 72, 67, 71, 68, 66, 69, 62, 58]} color="#f43f5e" />}
        onClick={() => navigate("/metrics/heart-rate")}
      />

      
    </div>
  );
}
